/**
 * DeliverableShowcase — the output, made tangible, in the exhibit system.
 * Two REAL documents produced by the platform's own generators on the demo case
 * (engine-grounded figures: BI net $155,309.37 footed; property RCV $257,000 /
 * ACV $173,500 and EPI as below-total disclosure lines). Execution fields
 * (letterhead, signer, notary) are an illustrative executed example.
 *
 * K3 exhibit grammar: the "44" is the one orange decisive figure (serif), the
 * families are a ruled ledger (mono index, no check chrome), the documents are
 * framed exhibits, one shared CTA. Atoms: eyebrow / 44 / CTA.
 */

import { PrimaryCTA, Provenance, SectionHeader } from './sectionPrimitives';

const HAIR = { borderColor: 'var(--color-hairline)' } as const;

// Deliverable families (what the platform's generators + sub-types produce —
// ~44 distinct court-ready documents in all). Source: docs/DELIVERABLES_CATALOG.md.
const DELIVERABLE_FAMILIES: Array<{ group: string; detail: string }> = [
  { group: 'Demand letters', detail: 'initial · follow-up · bad-faith · final · appraisal' },
  { group: 'Sworn Proof of Loss', detail: 'sworn · partial · amended · supplemental' },
  { group: 'Coverage correspondence', detail: 'EUO memo · RoR response · coverage analysis' },
  { group: 'Expert & Daubert', detail: 'expert report · defense matrix · evidence checklist' },
  { group: 'Workbooks & exhibits', detail: 'WB1/WB2 · reconciliation · Bates package' },
  { group: 'Court-ready bundles', detail: 'narrative/court PDF · DOCX · audit trail' },
];

const DOC_COUNT = 44;

const DOCS: Array<{ src: string; alt: string; label: string }> = [
  {
    src: '/sample-proof-of-loss.png',
    alt: 'Sworn Statement in Proof of Loss for Summit Ridge Restaurant Group — generated by The Figure Co., signed and notarized.',
    label: 'Exhibit A — Sworn Proof of Loss',
  },
  {
    src: '/sample-demand-letter.png',
    alt: 'Demand letter for Summit Ridge Restaurant Group — generated by The Figure Co. on firm letterhead.',
    label: 'Exhibit B — Demand letter',
  },
];

export default function DeliverableShowcase() {
  return (
    <section id="deliverable" className="border-t border-hairline">
      <div className="mx-auto max-w-[1400px] px-5 py-16 sm:px-8 lg:px-12 lg:py-24">
        <SectionHeader
          eyebrow="The deliverable"
          title="Court-ready the moment it’s signed"
          lede="Every figure traces to an engine output — the sworn amount footed to the net business-interruption loss, property and extended-indemnity disclosed separately, exactly as the policy requires."
        />

        <div className="mt-12 grid gap-12 lg:grid-cols-[0.92fr_1.08fr] lg:gap-16">
          {/* Left: the decisive figure + the ledger of families */}
          <div>
            <div className="flex items-baseline gap-5 border-y py-6" style={HAIR}>
              <span className="font-heading text-6xl leading-none text-brand-text">{DOC_COUNT}</span>
              <span className="font-mono text-[11px] uppercase leading-relaxed tracking-[0.16em] text-text-muted">
                Court-ready documents per claim file · generated in minutes · signed · served
              </span>
            </div>

            <ul className="mt-2 border-b" style={HAIR}>
              {DELIVERABLE_FAMILIES.map((f, i) => (
                <li key={f.group} className="grid grid-cols-[2.5rem_1fr] gap-4 border-t py-4" style={HAIR}>
                  <span className="pt-1 font-mono text-[11px] text-text-dim">{String(i + 1).padStart(2, '0')}</span>
                  <div className="min-w-0">
                    <h3 className="font-heading text-lg leading-snug tracking-tight text-text-primary">{f.group}</h3>
                    <p className="mt-1 font-mono text-[11px] leading-relaxed text-text-dim">{f.detail}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-10">
              <PrimaryCTA />
            </div>
          </div>

          {/* Right: two REAL generated documents, framed as exhibits */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {DOCS.map((doc) => (
              <figure key={doc.src} className="m-0">
                <img
                  src={doc.src}
                  alt={doc.alt}
                  width={860}
                  height={1113}
                  loading="lazy"
                  className="aspect-[8.5/11] w-full border object-cover object-top"
                  style={HAIR}
                />
                <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-text-dim">
                  {doc.label}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>

        <Provenance note={`Two of ${DOC_COUNT} — same case file, same engine figures`} />
      </div>
    </section>
  );
}
